import * as React from 'react'
import { Popup } from '@/ui/overlays/Popup'
import {
  ChartScanDetail,
  useAssignChartScans,
  useDeleteDiscs,
  useUploadChartScan,
} from '@/lib/api/company/chartScan'
import { StepUpload } from './StepUpload'
import { StepScan } from './scanner/StepScan'
import { ScanAssignment, StepAssign } from './StepAssign'
import { StepReviewDiscs } from './StepReviewDiscs'
import { fmt, useChartT } from './i18n'

// WPF ma dwie komendy - ReadDataFromTachoChartCommand (plik z dysku) i
// ReadDataFromScannerCommand (skaner) - które zbiegają się w ReadLoadedImages.
// Tutaj tak samo: różni się tylko pierwszy krok, od 'assign' w dół wszystko wspólne.
export type ChartImportSource = 'file' | 'scanner'

type Props = {
  isOpen?: boolean
  handleClose?: () => void
  companyId: string
  source: ChartImportSource
  onSuccess?: () => void
}

type Step = 'input' | 'processing' | 'assign' | 'review'

export const ChartImportPopupContainer = ({
  isOpen,
  handleClose,
  companyId,
  source,
  onSuccess,
}: Props) => {
  const T = useChartT()
  const [step, setStep] = React.useState<Step>('input')
  const [scans, setScans] = React.useState<ChartScanDetail[]>([])
  const [error, setError] = React.useState<string | null>(null)
  const [warning, setWarning] = React.useState<string | null>(null)
  const [progress, setProgress] = React.useState<{ done: number; total: number } | null>(
    null,
  )

  const upload = useUploadChartScan(companyId)
  const assign = useAssignChartScans(companyId)
  const discard = useDeleteDiscs(companyId)

  const reset = () => {
    setStep('input')
    setScans([])
    setError(null)
    setWarning(null)
    setProgress(null)
  }

  const close = () => {
    reset()
    handleClose?.()
  }

  const errorMessage = (e: any, fallback: string) => {
    const data = e?.response?.data
    return typeof data === 'string'
      ? data
      : data?.Message ?? data?.message ?? e?.message ?? fallback
  }

  // WPF saves every file picked in the (multiselect) dialog and then processes them
  // all — we do the same, one upload per file, and keep going if a single file fails
  // so one bad scan doesn't discard the rest of the selection.
  const handleFiles = async (files: File[]) => {
    setError(null)
    setWarning(null)
    setStep('processing')
    setProgress({ done: 0, total: files.length })

    const uploaded: ChartScanDetail[] = []
    const failed: string[] = []

    for (const file of files) {
      try {
        const res = await upload.mutateAsync(file)
        uploaded.push(res.detail)
      } catch (e: any) {
        failed.push(`${file.name}: ${errorMessage(e, T('uploadFailedShort', 'błąd wgrywania'))}`)
      }
      setProgress((p) => (p ? { ...p, done: p.done + 1 } : p))
    }

    setProgress(null)

    if (uploaded.length === 0) {
      setError(failed.join('\n') || T('uploadFailed', 'Błąd wgrywania pliku'))
      setStep('input')
      return
    }

    if (failed.length > 0) {
      setWarning(
        fmt(
          T('someFilesFailed', 'Nie udało się wczytać {0} z {1} plików — {2}'),
          failed.length,
          files.length,
          failed.join('; '),
        ),
      )
    }
    setScans(uploaded)
    setStep('assign')
  }

 
  const handleDiscardDisc = async (scanId: number, discId: number) => {
    await discard.mutateAsync({ scanId, discId })
    setScans((prev) =>
      prev.map((s) =>
        s.scanId === scanId
          ? { ...s, discs: s.discs.filter((d) => d.discId !== discId) }
          : s,
      ),
    )
  }

  const handleAssignContinue = async (assignments: ScanAssignment[]) => {
    if (assignments.length === 0) {
      handleFinish()
      return
    }
    try {
      const refreshed: ChartScanDetail[] = []
      for (const assignment of assignments) {
        refreshed.push(await assign.mutateAsync(assignment))
      }
      setScans(refreshed)
      setStep('review')
    } catch (e: any) {
      setError(errorMessage(e, T('assignSaveFailed', 'Błąd zapisu przypisania')))
    }
  }

  const handleFinish = () => {
    onSuccess?.()
    close()
  }

  return (
    <>
      <Popup
        open={!!isOpen && step !== 'review'}
        onClose={close}
        contentClassName="tw-w-[95vw] tw-max-w-[1800px]"
        closeOnBackdrop={false}
      >
        {step === 'input' && source === 'file' && (
          <StepUpload
            onFilesSelected={handleFiles}
            isUploading={upload.isPending}
            error={error}
          />
        )}

        {step === 'input' && source === 'scanner' && (
          <StepScan onCancel={close} onScanned={handleFiles} uploadError={error} />
        )}

        {step === 'processing' && (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-p-12">
            <div className="tw-h-10 tw-w-10 tw-animate-spin tw-rounded-full tw-border-4 tw-border-slate-200 tw-border-t-blue-600" />
            <p className="tw-text-sm tw-text-slate-700">
              {progress && progress.total > 1
                ? fmt(
                    T('detectingMulti', 'Wykrywam tarczki na skanach... ({0}/{1})'),
                    progress.done,
                    progress.total,
                  )
                : T('detectingSingle', 'Wykrywam tarczki na skanie...')}
            </p>
          </div>
        )}

        {step === 'assign' && scans.length > 0 && (
          <StepAssign
            companyId={companyId}
            scans={scans}
            isSaving={assign.isPending}
            onCancel={close}
            onContinue={handleAssignContinue}
            onDiscardDisc={handleDiscardDisc}
            warning={warning}
          />
        )}
      </Popup>

      {isOpen && step === 'review' && scans.length > 0 && (
        <StepReviewDiscs
          companyId={companyId}
          scans={scans}
          // Deliberately no onSaved: the host's onSuccess navigates away from the
          // imports page, and a scan can hold several discs. Saving disc 1 of 5
          // would abandon the rest. handleFinish fires onSuccess once, after the
          // last disc of the last file is done.
          onFinish={handleFinish}
        />
      )}
    </>
  )
}
